import React, { useEffect, useRef } from 'react';
import { Box, FormHelperText, Stack, TextField } from '@mui/material';

interface OTPInputProps {
	length?: number;
	value: string;
	error?: string;
	onChange: (value: string) => void;
	onComplete?: () => void;
}

export const OTPInput: React.FC<OTPInputProps> = ({
	length = 6,
	value,
	error,
	onChange,
	onComplete,
}) => {
	const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

	useEffect(() => {
		if (value.length === length) {
			onComplete?.();
		}
	}, [value, length]);

	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number
	) => {
		const newValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 1);
		const valueArr = value.split('');

		valueArr[index] = newValue;
		const updatedValue = valueArr.join('');
		onChange(updatedValue);

		if (newValue && index < length - 1) {
			inputsRef.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, index: number) => {
		if (e.key === 'Backspace' && !value[index] && index > 0) {
			inputsRef.current[index - 1]?.focus();
		}
	};

	const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>, index: number) => {
		e.preventDefault();
		const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
		if (!pastedData) return;

		if (pastedData.length === length) {
			onChange(pastedData);
			inputsRef.current[length - 1]?.focus();
			return;
		}

		const valueArr = value.split('');
		let nextIndex = index;

		for (let i = 0; i < pastedData.length; i++) {
			if (nextIndex < length) {
				valueArr[nextIndex] = pastedData[i];
				nextIndex++;
			}
		}

		const updatedValue = valueArr.join('');
		onChange(updatedValue);

		if (nextIndex < length) {
			inputsRef.current[nextIndex]?.focus();
		} else {
			inputsRef.current[length - 1]?.focus();
		}
	};

	return (
		<Stack direction="column" width="100%">
			<Box display="flex" width="100%" gap={1}>
				{Array.from({ length }).map((_, i) => (
					<TextField
						key={i}
						inputRef={(el: HTMLInputElement | null) => {
							inputsRef.current[i] = el;
						}}
						value={value[i] || ''}
						onChange={(e) => handleChange(e, i)}
						onKeyDown={(e) => {
							if (
								!/^[0-9]$/.test(e.key) &&
								!['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(
									e.key
								) &&
								!e.metaKey &&
								!e.ctrlKey
							) {
								e.preventDefault();
							}
							handleKeyDown(e, i);
						}}
						onPaste={(e) => handlePaste(e, i)}
						type="tel"
						error={!!error}
						inputProps={{
							maxLength: 1,
							inputMode: 'numeric',
							style: {
								textAlign: 'center',
								fontSize: '1.4rem',

								height: '72px',
								padding: 0,
							},
						}}
						sx={{
							'& fieldset': {
								borderRadius: '10px',
							},
							'& input': {
								textAlign: 'center',
								MozAppearance: 'textfield',
							},
							'& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
								WebkitAppearance: 'none',
								margin: 0,
							},
						}}
					/>
				))}
			</Box>
			{error ? (
				<FormHelperText
					error
					sx={(theme) => ({
						color: '#d32f2f',
						fontSize: 'var(--font-size-caption)',
						margin: 0,
						padding: 0,
						marginTop: theme.spacing(0.5),
					})}
				>
					{error}
				</FormHelperText>
			) : null}
		</Stack>
	);
};
